import { cn } from "@repo/shared/utils";
import {
  ChevronRightIcon,
  EllipsisIcon,
  FileIcon,
  FolderIcon,
  FolderOpenIcon,
  RotateCcwIcon,
} from "lucide-react";
import { createContext, useContext, useEffect, useState } from "react";
import { useFutureCallback } from "./hooks/useFutureCallback";
import { Button } from "./Button";

const chevronStyles = /*@tw*/ "w-3 h-3 transition-transform";
const iconStyles = /*@tw*/ "w-4 h-4 text-primary";
const fileStyles = /*@tw*/ "text-slate-600";
const folderStyles = /*@tw*/ "text-primary";
const ellipsisStyles = /*@tw*/ "text-slate-400 animate-pulse";
const refreshStyles = /*@tw*/ "text-black";

interface DirectoryTreeContextInterface<T, S> {
  disabled: boolean;
  showFiles: boolean;
  showRefresh: boolean;
  pick?: "file" | "folder" | "all";
  selection: S[];
  select: (item: T) => void;
  listDirectory: (parent: T | null) => T[] | Promise<T[]>;
  entryDetails: (entry: T) => {
    label: string;
    type: "file" | "folder";
    key: string;
  };
  entrySelectionId: (entry: T) => S;
  computeClassName?: (
    component: DirectoryTreeComponentType,
    entry: T | null
  ) => string | boolean | null | undefined;
}

const DirectoryTreeContext = createContext<DirectoryTreeContextInterface<
  unknown,
  unknown
> | null>(null);

// Base tree item
interface TreeItemProps {
  disabled?: boolean;
  icon: React.ReactNode;
  label: React.ReactNode;
  expandable?: boolean;
  expanded?: boolean;
  showRefresh?: boolean;
  onRefresh?: () => void;
  depth: number;
  onToggleExpanded?: () => void;
  showSelect?: boolean;
  selected?: boolean;
  onSelectClick?: () => void;
  className?: string;
  computeClassName?: (
    component: DirectoryTreeComponentType
  ) => string | boolean | null | undefined;
}

const TreeItem = ({
  disabled = false,
  icon,
  label,
  expandable = false,
  expanded = false,
  showRefresh = false,
  onRefresh,
  depth,
  onToggleExpanded,
  showSelect = false,
  selected = false,
  onSelectClick,
  className,
  computeClassName,
}: TreeItemProps) => {
  return (
    <div
      style={{ paddingLeft: depth * 16 }}
      className={cn(
        "tree-item-row flex flex-row justify-start items-center p-0.5 rounded-md hover:bg-slate-300/20 cursor-pointer",
        className
      )}
      onClick={(evt) => {
        const button = (evt.target as HTMLElement).closest(
          "button, .tree-item-row"
        );
        if (!button || button.tagName.toLowerCase() !== "button") {
          onToggleExpanded?.();
        }
      }}
    >
      <div className="w-5 h-5 flex flex-row justify-center items-center">
        {expandable && (
          <ChevronRightIcon
            className={cn(
              chevronStyles,
              expanded && "rotate-90",
              computeClassName?.("icon.chevron")
            )}
          />
        )}
      </div>

      <div className="w-5 h-5 flex flex-row justify-center items-center">
        {icon}
      </div>

      <p
        className={cn(
          "w-0 flex-grow truncate mx-2",
          selected && "font-bold",
          computeClassName?.("label")
        )}
        title={typeof label === "string" ? label : ""}
      >
        {label}
      </p>

      {showRefresh && (
        <Button
          variant="ghost"
          className={cn(
            "w-6 h-6 p-0 mr-5 flex flex-row justify-center items-center",
            computeClassName?.("button.refresh")
          )}
          onClick={onRefresh}
        >
          <RotateCcwIcon
            className={cn(
              iconStyles,
              refreshStyles,
              computeClassName?.("icon.refresh")
            )}
          />
        </Button>
      )}

      {showSelect && (
        <Button
          disabled={disabled}
          variant={selected ? undefined : "accent"}
          className={cn(
            "py-0 px-5 h-6 text-sm",
            computeClassName?.("button.select")
          )}
          onClick={onSelectClick}
        >
          SELECT
        </Button>
      )}
    </div>
  );
};

// Tree folder's children component
interface FolderChildrenProps<T> {
  expanded: boolean;
  parent: T | null;
  depth: number;
}

const FolderChildren = <T, S>({
  expanded,
  parent,
  depth,
}: FolderChildrenProps<T>) => {
  const [children, setChildren] = useState<React.ReactNode[] | null>(null);

  const { listDirectory, entryDetails } = useContext(
    DirectoryTreeContext
  )! as DirectoryTreeContextInterface<T, S>;

  const updateChildren = useFutureCallback(
    async (canceler: { cancel: boolean }) => {
      const entries = await listDirectory(parent);
      if (canceler.cancel) return;

      const children = entries
        .map((entry) => ({ ...entryDetails(entry), entry }))
        .sort((a, b) =>
          a.type !== b.type
            ? a.type === "folder"
              ? -1
              : 1
            : a.label !== b.label
              ? a.label < b.label
                ? -1
                : 1
              : 0
        )
        .map(({ label, type, key, entry }) => {
          return type === "file" ? (
            <TreeFile key={key} label={label} depth={depth} entry={entry} />
          ) : (
            <TreeFolder key={key} label={label} depth={depth} entry={entry} />
          );
        });

      setChildren(children);
    }
  );

  useEffect(() => {
    if (children || !expanded) return;

    const canceler = { cancel: false };

    updateChildren(canceler);

    return () => {
      canceler.cancel = true;
    };
  }, [children, expanded, updateChildren]);

  return (
    <div className={cn("w-full", expanded || "hidden")}>
      {children ?? <TreeLoading label="Loading" depth={depth} />}
    </div>
  );
};

// Tree loading node
interface TreeLoadingProps {
  label: React.ReactNode;
  depth: number;
}

const TreeLoading = <T, S>({ label, depth }: TreeLoadingProps) => {
  const { computeClassName } = useContext(
    DirectoryTreeContext
  ) as DirectoryTreeContextInterface<T, S>;

  return (
    <TreeItem
      icon={
        <EllipsisIcon
          className={cn(
            iconStyles,
            ellipsisStyles,
            computeClassName?.("icon.ellipsis", null)
          )}
        />
      }
      label={label}
      depth={depth}
      className={cn(
        "italic opacity-70",
        computeClassName?.("item.loading", null)
      )}
    />
  );
};

// Tree file node
interface TreeFileProps<T> {
  entry: T;
  label: React.ReactNode;
  depth: number;
}

const TreeFile = <T, S>({ label, depth, entry }: TreeFileProps<T>) => {
  const {
    disabled,
    pick,
    selection,
    select,
    entrySelectionId,
    computeClassName,
  } = useContext(DirectoryTreeContext)! as DirectoryTreeContextInterface<T, S>;
  const canPickFile = pick === "file" || pick === "all";

  return (
    <TreeItem
      disabled={disabled}
      icon={
        <FileIcon
          className={cn(
            iconStyles,
            fileStyles,
            computeClassName?.("icon.file", entry)
          )}
        />
      }
      label={label}
      depth={depth}
      showSelect={canPickFile}
      selected={selection.includes(entrySelectionId(entry))}
      onSelectClick={() => canPickFile && select(entry)}
      className={cn(computeClassName?.("item.file", entry))}
      computeClassName={
        computeClassName && ((type) => computeClassName(type, entry))
      }
    />
  );
};

// Tree folder node
interface TreeFolderProps<T> {
  entry: T;
  label: React.ReactNode;
  depth: number;
}

const TreeFolder = <T, S>({ label, depth, entry }: TreeFolderProps<T>) => {
  const [expanded, setExpanded] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const {
    disabled,
    pick,
    selection,
    select,
    entrySelectionId,
    showRefresh,
    computeClassName,
  } = useContext(DirectoryTreeContext)! as DirectoryTreeContextInterface<T, S>;
  const canPickFolder = pick === "folder" || pick === "all";

  const iconClasses = cn(iconStyles, folderStyles);

  return (
    <div>
      <TreeItem
        disabled={disabled}
        expandable
        expanded={expanded}
        showRefresh={showRefresh}
        icon={
          expanded ? (
            <FolderOpenIcon
              className={cn(
                iconClasses,
                computeClassName?.("icon.folder.open", entry)
              )}
            />
          ) : (
            <FolderIcon
              className={cn(
                iconClasses,
                computeClassName?.("icon.folder.closed", entry)
              )}
            />
          )
        }
        label={label}
        depth={depth}
        onRefresh={() => {
          setExpanded(true);
          setRefreshKey((key) => key + 1);
        }}
        onToggleExpanded={() => setExpanded((expanded) => !expanded)}
        showSelect={canPickFolder}
        selected={selection.includes(entrySelectionId(entry))}
        onSelectClick={() => canPickFolder && select(entry)}
        className={cn(computeClassName?.("item.folder", entry))}
        computeClassName={
          computeClassName && ((type) => computeClassName(type, entry))
        }
      />
      <FolderChildren
        key={refreshKey}
        expanded={expanded}
        parent={entry}
        depth={depth + 1}
      />
    </div>
  );
};

export type DirectoryTreeComponentType =
  // icons
  | "icon.file"
  | "icon.folder.open"
  | "icon.folder.closed"
  | "icon.ellipsis"
  | "icon.chevron"
  | "icon.refresh"
  // items
  | "item.file"
  | "item.folder"
  | "item.loading"
  // buttons
  | "button.refresh"
  | "button.select"
  // label
  | "label";

// Main directory tree component
export interface ExpandableDirectoryTreeProps<T, S> {
  disabled?: boolean;
  showFiles?: boolean;
  showRefresh?: boolean;
  pick?: "file" | "folder" | "all";
  multiple?: boolean;
  selection: S[];
  onSelect?: (item: T, selecting: boolean, newSelection: S[]) => void;
  listDirectory: (parent: T | null) => T[] | Promise<T[]>;
  entryDetails: (entry: T) => {
    label: string;
    type: "file" | "folder";
    key: string;
  };
  entrySelectionId: (entry: T) => S;
  className?: string;
  computeClassName?: (
    component: DirectoryTreeComponentType,
    entry: T | null
  ) => string | boolean | null | undefined;
}

export const ExpandableDirectoryTree = <T, S>({
  disabled = false,
  showFiles = true,
  showRefresh = false,
  pick = "all",
  multiple = false,
  selection,
  onSelect,
  listDirectory,
  entryDetails,
  entrySelectionId,
  className,
  computeClassName,
}: ExpandableDirectoryTreeProps<T, S>) => {
  listDirectory = useFutureCallback(listDirectory);
  entryDetails = useFutureCallback(entryDetails);
  entrySelectionId = useFutureCallback(entrySelectionId);

  const select = useFutureCallback((entry: T) => {
    const id = entrySelectionId(entry);

    const isSelected = selection.includes(id);

    let newSelection: S[];
    if (multiple) {
      newSelection = isSelected
        ? selection.filter((item) => item !== id) // already selected: remove
        : [...selection, id]; // not selected: add
    } else {
      newSelection = isSelected ? [] : [id];
    }

    onSelect?.(entry, !isSelected, newSelection);
  });

  const context: DirectoryTreeContextInterface<T, S> = {
    disabled,
    showFiles,
    showRefresh,
    pick,
    selection,
    select,
    listDirectory,
    entryDetails,
    entrySelectionId,
    computeClassName,
  };

  return (
    <DirectoryTreeContext.Provider
      value={context as DirectoryTreeContextInterface<unknown, unknown>}
    >
      <div className={cn("flex flex-col items-start justify-start", className)}>
        <FolderChildren expanded depth={0} parent={null} />
      </div>
    </DirectoryTreeContext.Provider>
  );
};
